// apps/web/src/features/commercial-proposals/public/PublicProposalSheet.tsx
//
// @aiPurpose render the resolved public DTO on the customer sheet (#1217)
//   through the shared `CommercialProposalCustomerSheet`, so the public sheet
//   and the internal preview are the IDENTICAL document. Every value the
//   backend ships is a pre-formatted DISPLAY STRING rendered VERBATIM — the
//   public projection never carries raw coefficients or ids to recompute.
// @aiSecurity notes preserve newlines via CSS `white-space: pre-wrap` inside
//   the shared renderer, never `dangerouslySetInnerHTML`. There is
//   intentionally NO edit / publish / revoke / analytics / internal control
//   anywhere on this surface — the only action is the customer PDF download.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommercialProposalPublicDto } from '@magimp/shared';

import { Button } from '../../../ui/index.js';
import { CommercialProposalCustomerSheet } from '../sheet/CommercialProposalCustomerSheet.js';
import { triggerBrowserDownload } from '../download.js';
import { downloadPublicProposalPdf } from './publicApi.js';
import { PublicProposalPhoto } from './PublicProposalPhoto.js';

export function PublicProposalSheet({
  token,
  dto,
}: {
  token: string;
  dto: CommercialProposalPublicDto;
}) {
  return (
    <main
      data-testid="cp-public-sheet"
      style={{
        minHeight: '100vh',
        background: 'var(--kp-paper)',
        padding: 'var(--s-7)',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <CommercialProposalCustomerSheet
        dto={dto}
        photoSlot={dto.hasPhoto ? <PublicProposalPhoto token={token} /> : null}
        actionsSlot={<PdfDownloadButton token={token} productName={dto.productName} />}
        grandTotalTestId="cp-public-grand-total"
      />
    </main>
  );
}

function PdfDownloadButton({ token, productName }: { token: string; productName: string }) {
  const { t } = useTranslation('commercialProposalsPublic');
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const onDownloadPdf = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const blob = await downloadPublicProposalPdf(token);
      triggerBrowserDownload(blob, `${productName || 'proposal'}.pdf`);
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Button
        kind="primary"
        disabled={downloading}
        onClick={() => void onDownloadPdf()}
        data-testid="cp-public-download-pdf"
      >
        {t('downloadPdf')}
      </Button>
      {downloadFailed ? (
        <p
          role="alert"
          data-testid="cp-public-download-error"
          style={{
            marginTop: 'var(--s-3)',
            fontSize: 'var(--fz-12)',
            color: 'var(--status-error-fg)',
          }}
        >
          {t('downloadError')}
        </p>
      ) : null}
    </>
  );
}
